import type { CardConfig } from '../types';
import { Phone, Mail, MapPin, Save, Share2, Download, Link2, QrCode, ChevronRight } from 'lucide-react';
import PageRenderer from './PageRenderers';
import { PreviewableImage } from './ImagePreview';
import PreviewThemeProvider, { usePreviewTheme, withAlpha } from './PreviewTheme';

const CARD_HEIGHT = 200;

function BusinessCardPreviewInner({ config }: { config: CardConfig }) {
  const theme = usePreviewTheme();
  const { displayFields, cardBgType, cardBg, cardLayout, pageBg, logo, mainTitle, subTitle, subTitleEn, contact,themeColor } = config;

  const avatarInitial = (contact.phone || mainTitle || '?').charAt(0);
  const phoneDisplay = contact.phone || '暂无电话';
  const emailDisplay = contact.email || '暂无邮箱';
  const addressDisplay = contact.address || '暂无地址';

  const cardStyle: React.CSSProperties = {
    height: CARD_HEIGHT,
    backgroundImage: cardBgType === 'custom' && cardBg ? `url(${cardBg})` : 'none',
    backgroundColor: cardBgType === 'default' ? '#ffffff' : 'transparent',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
  };

  const getContactItems = () => {
    const items: { icon: JSX.Element; text: string }[] = [];
    if (displayFields.phone) items.push({ icon: <Phone className="w-3.5 h-3.5" />, text: phoneDisplay });
    if (displayFields.email) items.push({ icon: <Mail className="w-3.5 h-3.5" />, text: emailDisplay });
    if (displayFields.companyAddress) items.push({ icon: <MapPin className="w-3.5 h-3.5" />, text: addressDisplay });
    return items;
  };

  const renderCard = () => {
    switch (cardLayout) {
      case 'left':
        return (
          <div style={{ ...cardStyle, height: 200 }} className="w-full flex flex-col">
            {displayFields.company && (
              <div className="w-full px-3 py-2.5 text-sm">
                XXXXXXXX公司
              </div>
            )}
            <div className="flex flex-1 items-center px-3 gap-3">
              <div className="flex-1 flex flex-col min-w-0 gap-1.5">
                <div className="flex flex-wrap items-baseline">
                  {displayFields.name && (
                    <div className="text-base font-bold text-gray-900 whitespace-nowrap shrink-0 mr-2">
                      张XX
                    </div>
                  )}
                  {displayFields.position && (
                    <div className="text-xs text-gray-500 flex-1 word-break-break-all">
                      职位XX
                    </div>
                  )}
                </div>
                {displayFields.phone && (
                  <div className="text-xs text-gray-600 truncate flex">
                    <Phone className="w-3.5 h-3.5" />：1XXXXXXXXXXXX
                  </div>
                )}
                {displayFields.email && (
                  <div className="text-xs text-gray-600 truncate flex">
                    <Mail className="w-3.5 h-3.5" />：XXXX@XXX
                  </div>
                )}
              </div>
              {displayFields.avatar && (
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-xl font-bold shrink-0 shadow-sm" style={{ background: theme.themeGradient }}>
                  {avatarInitial}
                </div>
              )}
            </div>
            {displayFields.companyAddress && (
              <div className="w-full px-3 py-2 text-xs">
                XX省XX市XX区XXXXXX
              </div>
            )}
          </div>
        );

      case 'center':
        return (
          <div style={{ ...cardStyle, height: 200 }} className="w-full flex flex-col justify-between p-3">
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col min-w-0">
                {displayFields.name && (
                  <div className="text-base font-bold text-gray-900 whitespace-nowrap shrink-0">
                    张XX
                  </div>
                )}
                {displayFields.company && (
                  <div className="pt-1.5 text-xs">
                    XXXXXXXX公司
                  </div>
                )}
                {displayFields.position && (
                  <div className="pt-1.5 text-xs text-gray-500 flex-1 word-break-break-all">
                  职位XX
                  </div>
                )}
              </div>
              {displayFields.avatar && (
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-xl font-bold shrink-0 shadow-sm" style={{ background: theme.themeGradient }}>
                  {avatarInitial}
                </div>
              )}
            </div>
            <div className="flex flex-col">
              {displayFields.phone && (
                <div className="pb-1.5 text-xs flex">
                  <Phone className="w-3.5 h-3.5" />：1XXXXXXXXXXXX
                </div>
              )}
              {displayFields.email && (
                <div className="pb-1.5 text-xs flex">
                  <Mail className="w-3.5 h-3.5" />：XXXX@XXX
                </div>
              )}
              {displayFields.companyAddress && (
                <div className="text-xs flex">
                  <MapPin className="w-3.5 h-3.5" />：XX省XX市XX区XXXXXX  
                </div>
              )}
            </div>
          </div>
        );

      case 'right':
        return (
          <div style={{ ...cardStyle, height: 200 }} className="w-full flex flex-col p-3">
            <div className='w-full flex items-center justify-center '>
              {displayFields.avatar && (
                <div className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-xl font-bold shrink-0 shadow-sm mr-2" style={{ background: theme.themeGradient }}>
                  {avatarInitial}
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                {displayFields.name && (
                  <div className="text-base font-bold text-gray-900 whitespace-nowrap">
                    张XX
                  </div>
                )}
                {displayFields.position && (
                  <div className="text-xs text-gray-500">
                    职位XX
                  </div>
                )}
                {displayFields.company && (
                  <div className="text-[11px] text-gray-600 mt-1">
                    XXXXXXXX公司
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-gray-100 w-full px-2">
              {displayFields.phone && (
                <div className="text-[11px] text-gray-600 flex items-center justify-center gap-1">
                  <span>1XXXXXXXXXXXX</span>
                </div>
              )}
              {displayFields.email && (
                <div className="text-[11px] text-gray-600 flex items-center justify-center gap-1">
                  <span>XXXX@XXX</span>
                </div>
              )}
              {displayFields.companyAddress && (
                <div className="text-[11px] text-gray-600 flex items-center justify-center gap-1">
                  <span>XX省XX市XX区XXXXXX</span>
                </div>
              )}
            </div>
          </div>
        );

      case 'preview-4':
        return (
          <div style={{ ...cardStyle, height: 'auto', minHeight: 200 }} className="w-full flex">
            {displayFields.avatar && (
              <div
                className="w-2/5 flex items-center justify-center p-3"
                style={{
                  backgroundImage: cardBgType === 'custom' && cardBg ? `url(${cardBg})` : theme.buttonGradient,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                <div className="w-full aspect-square rounded-lg bg-white/30 backdrop-blur flex items-center justify-center text-white text-3xl font-bold shadow-md">
                  {avatarInitial}
                </div>
              </div>
            )}
            <div className="flex-1 p-3.5 flex flex-col justify-center gap-1.5 min-w-0">
              {displayFields.name && (
                <div className="text-lg font-bold text-gray-900 truncate">张XX</div>
              )}
              {displayFields.position && (
                <div className="text-xs text-gray-500 word-break-break-all">职位XX</div>
              )}
              {displayFields.company && (
                <div className="text-xs text-gray-500 word-break-break-all">XXXXXXXXX公司</div>
              )}
              <div className="border-t border-gray-100 my-0.5" />
              <div className="space-y-1 text-xs text-gray-600">
                {displayFields.phone && (
                  <div className="text-xs flex"><Phone className="w-3.5 h-3.5" />：1XXXXXXXXXXXX</div>
                )}
                {displayFields.email && (
                  <div className="text-xs flex"><Mail className="w-3.5 h-3.5" />：XXXX@XXX</div>
                )}
                {displayFields.companyAddress && (
                  <div className="text-xs flex"><MapPin className="w-3.5 h-3.5" />：XX省XX市XX区XXXXXX</div>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return <div style={cardStyle} className="flex items-center justify-center text-gray-400">选择一个布局</div>;
    }
  };

  return (
    <div className="relative overflow-hidden h-full overflow-y-auto" style={{ width: 375, backgroundColor: '#f5f6fa' }}> 
      {pageBg && (
        <div className='absolute top-0 left-0 w-full z-0'>
          <PreviewableImage  
              src={pageBg}
              className='w-full' 
              style={{ width: '100%', height: 'auto' }}
          />
        </div>
      )}
      <div className='relative top-0 left-0 w-full p-4 z-10' style={{ paddingTop: '16px' }}>
        {/* 页面头部区域（主题配色横幅） */}
        <div className="text-center mb-5 rounded-xl px-4 py-5">
          {logo && (
            <PreviewableImage src={logo} alt="logo" className="mb-3" style={{ width: '88px', height: 'auto' }}  />
          )}
          <div className="text-xl font-bold tracking-wide" style={{ marginBottom: '8px', color: config.themeColor }}>{mainTitle || '主题'}</div>
          <div className="inline-flex items-center justify-center py-1" style={{ color: config.themeColor }}>
            <span className="text-[11px] whitespace-nowrap">{subTitle || '副主题'}</span>
          </div>
          {subTitleEn && (
            <div className="text-[9px] opacity-80 text-center mt-2" style={{ width: '85%', margin: '0 auto', color: config.themeColor, letterSpacing: '0.05em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{subTitleEn}</div>
          )}
        </div>

        {/* 名片展示区域 */}
        <div className="w-full p-2 bg-white rounded-xl" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
          {renderCard()}
        </div>

        {/* 功能配置 - 动态按钮 */}
        {(config.saveToContacts || config.enablePoster || config.enableMiniCode || config.enableShortLink) && (
          <div className="w-full mt-4">
            <div className="flex items-center gap-2">
              {config.saveToContacts && (
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-xl border border-gray-100 transition-all shadow-sm" style={{ borderColor: withAlpha(theme.themeColor, '4D') }}>
                  <Save className="w-3.5 h-3.5" style={{ color: theme.themeColor }} />
                  <span className="text-[11px]" style={{ color: theme.textNormalColor }}>存入通讯录</span>
                </button>
              )}
              {config.enablePoster && (
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-xl border border-gray-100 transition-all shadow-sm" style={{ borderColor: withAlpha(theme.themeColor, '4D') }}>
                  <Download className="w-3.5 h-3.5" style={{ color: theme.themeColor }} />
                  <span className="text-[11px]" style={{ color: theme.textNormalColor }}>生成海报</span>
                </button>
              )}
              {config.enableMiniCode && (
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-xl border border-gray-100 transition-all shadow-sm" style={{ borderColor: withAlpha(theme.themeColor, '4D') }}>
                  <QrCode className="w-3.5 h-3.5" style={{ color: theme.themeColor }} />
                  <span className="text-[11px]" style={{ color: theme.textNormalColor }}>小程序码</span>
                </button>
              )}
              {config.enableShortLink && (
                <button className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-white rounded-xl border border-gray-100 transition-all shadow-sm" style={{ borderColor: withAlpha(theme.themeColor, '4D') }}>
                  <Link2 className="w-3.5 h-3.5" style={{ color: theme.themeColor }} />
                  <span className="text-[11px]" style={{ color: theme.textNormalColor }}>分享链接</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs 页面内容区域 */}
        {config.selectedPages && config.selectedPages.length > 0 && (
          <div>
            <PageRenderer config={config} />
          </div>
        )}

        {/* 二维码区域 */}
        {(contact.qrcode || getContactItems().length > 0) && (
          <div className="mt-4 p-4 bg-white rounded-xl" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
            <div className="flex items-center gap-4">
              {contact.qrcode && (
                <div className="shrink-0">
                  <PreviewableImage src={contact.qrcode} alt="qr" className="w-24 h-24 rounded-xl border-2 border-white shadow-md" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="space-y-2 text-xs text-gray-600">
                  {displayFields.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5" style={{ color: theme.themeColor }} />
                      <span>{phoneDisplay}</span>
                    </div>
                  )}
                  {displayFields.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" style={{ color: theme.themeColor }} />
                      <span className="truncate">{emailDisplay}</span>
                    </div>
                  )}
                  {displayFields.companyAddress && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: theme.themeColor }} />
                      <span className="truncate">{addressDisplay}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 底部版权 */}
        <div className="mt-6 pb-4 text-center">
          <div className="flex items-center justify-center gap-2 text-[10px] text-gray-400">
            <span className="w-6 h-px bg-gray-300" />
            <span>感谢您的关注</span>
            <span className="w-6 h-px bg-gray-300" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BusinessCardPreview({ config }: { config: CardConfig }) {
  return (
    <PreviewThemeProvider>
      <BusinessCardPreviewInner config={config} />
    </PreviewThemeProvider>
  );
}
